import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ProductService } from 'src/app/core/api/product/product.service';
import { ProductRequestDTO } from 'src/app/shared/models/dtos/requests/product-request-dto/product-request-dto';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductDTO } from 'src/app/shared/models/dtos/responses/product-dto/product-dto';
import {
  atLeastOneHeaderValidator,
  specialCharValidator,
} from 'src/app/shared/validators/registration-validators';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { catchError, finalize, of } from 'rxjs';
import { RoutesKeys } from 'src/app/utils/routes';
import { SelectorData } from 'src/app/utils/selectorDataType';

export interface ProductSummary {
  name: string;
  image: string;
  family: string;
  headers: string[];
  oldHeaders: string[];
}

export interface ProductLabels {
  printerHeadersNote: string;
  printerFamilyNote: string;
  printerNote: string;
  participatingProduct: string;
  typeNameOfProduct: string;
  controlPanel: string;
  addProduct: string;
  editProduct: string;
  cancel: string;
  next: string;
  back: string;
  productNote: string;
  productReview: string;
  productNamePlaceholder: string;
  printerHeadersPlaceholder: string;
  selectPrinterFamily: string;
  productImage: string;
  create: string;
  headersDisclaimer: string;
  update: string;
  productUpdatedSuccess: string;
  productSaveError: string;
  productLoadError: string;
}

@Component({
  selector: 'app-add-edit-product',
  templateUrl: './add-edit-product.component.html',
  styleUrls: ['./add-edit-product.component.scss'],
})

/**
 * Component for adding and editing products.
 */
export class AddEditProductComponent implements OnInit {
  productForm = new FormGroup(
    {
      name: new FormControl<string>('', [
        Validators.required,
        specialCharValidator(),
        Validators.minLength(3),
        Validators.maxLength(255),
      ]),
      image: new FormControl<string>(''),
      family: new FormControl<undefined | string>(undefined, [
        Validators.required,
      ]),
      familyId: new FormControl<number | undefined>(undefined, [
        Validators.required,
      ]),
      headers: new FormControl<string[]>([]),
      oldHeaders: new FormControl<string[]>([]),
    },
    { validators: atLeastOneHeaderValidator }
  );
  productId?: number;
  product?: ProductDTO;
  loading = false;
  isEditing = false;
  labels = {
    printerHeadersNote: 'printer-headers-note',
    printerFamilyNote: 'printer-family',
    printerNote: 'printer-note',
    participatingProduct: 'participating-product',
    typeNameOfProduct: 'type-name-of-product',
    controlPanel: 'control-panel',
    addProduct: 'add-product',
    editProduct: 'edit-product',
    next: 'next-button',
    cancel: 'cancel',
    back: 'back',
    productNote: 'product-note',
    productReview: 'product-review',
    productNamePlaceholder: 'product-name-placeholder',
    printerHeadersPlaceholder: 'printer-headers-placeholder',
    selectPrinterFamily: 'select-printer-family',
    productImage: 'product-image',
    create: 'create',
    headersDisclaimer: 'product-headers-edit-disclaimer',
    update: 'update',
    printerFamilyBasename: 'business-series',
    productUpdatedSuccess: 'product-updated-success',
    productSaveError: 'product-save-error',
    productLoadError: 'product-load-error',
  };
  productFamilies: SelectorData[] = [];

  /**
   * Constructor
   */
  constructor(
    private readonly translationService: TranslationService,
    private productService: ProductService,
    private router: Router,
    private dialogService: DialogService,
    private activatedRoute: ActivatedRoute,
    private snackBarService: SnackBarService
  ) {}

  /**
   * Initializer
   */
  ngOnInit() {
    this.translationService.translateObject(this.labels);

    this.productService.getAllFamilyProduct().subscribe((families) => {
      this.productFamilies = families.map((family) => ({
        label: `${this.labels.printerFamilyBasename} ${family.name}`,
        value: family.name,
        id: family.id,
      }));
    });

    this.activatedRoute.params.subscribe((params) => {
      this.productId = params['id'];

      if (this.productId) {
        this.loading = true;

        this.productService
          .getProductById(this.productId)
          .pipe(
            catchError(() => {
              this.snackBarService.error(this.labels.productLoadError);
              return of(null);
            }),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe((product) => {
            if (!product) return;

            this.product = product;
            this.productForm.patchValue({
              ...product,
              headers: [],
              oldHeaders: product.headers,
            });
            this.productForm
              .get('familyId')
              ?.setValue(this.product.productFamily?.id);
            this.productForm.get('family')?.disable();
            this.productForm.markAsDirty();
          });
      }
    });
  }

  /**
   * Retrieves a summary of the product form values.
   * @return {ProductSummary} An object containing the product's name, image, family, and headers.
   */
  get productSummary(): ProductSummary {
    if (this.product) {
      return this.productForm.getRawValue() as ProductSummary;
    }
    return this.productForm.value as ProductSummary;
  }

  /**
   * Navigates back to the control panel.
   */
  goBack() {
    this.router.navigate([RoutesKeys.CONTROL_PANEL]);
  }

  /**
   * Save product
   */
  saveProduct() {
    if (this.productForm.invalid) {
      return;
    }

    this.loading = true;
    const dto = this.productForm.value as ProductRequestDTO;

    const request$ = this.product
      ? this.productService.updateProduct(dto, this.productId!)
      : this.productService.createProduct(dto);

    request$
      .pipe(
        catchError(() => {
          this.snackBarService.error(this.labels.productSaveError);
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((res) => {
        if (!res) return;
        if (this.product) {
          this.snackBarService.success(this.labels.productUpdatedSuccess);
          this.router.navigate([RoutesKeys.CONTROL_PANEL]);
          return;
        }
        this.dialogService.openProductCreatedModal(
          res,
          () => this.router.navigate([RoutesKeys.CONTROL_PANEL]),
          () => {
            this.router.navigate([RoutesKeys.REWARD_DETAIL]);
          }
        );
      });
  }

  /**
   * Updates the editing state of the component.
   * @param {boolean} editing - The new editing state.
   */
  onEditing(editing: boolean): void {
    this.isEditing = editing;
  }
}
